import type { IntegrationMilestone } from "../../../../../../pages/hid-admin/dashboard/partner-organizations/partner-list/integration-status/integrationStatusTypes";
import {
  isNotApplicableStatus,
  mapPhaseToMilestoneState,
  NOT_APPLICABLE_META_LABEL,
} from "../../../../../../common/components/integration-timeline/timelineUtils";
import {
  Integration,
  TimelineEntry,
} from "../../../../../../redux/slices/projects/Types";
import { IntegrationStatusLabel } from "../integration-steps/shared/StatusTag";
import { getTimelineEntries } from "../integration-steps/shared/timeline-helpers";
import { getOnboardingCurrentStatus } from "../integration-steps/onboarding";
import { getTrainingCurrentStatus } from "../integration-steps/training";
import { getDevelopmentCurrentStatus } from "../integration-steps/development";
import { getValidationCurrentStatus } from "../integration-steps/validation";

const formatTimelineDate = (date: string | null | undefined): string | undefined => {
  if (!date || date.trim() === "" || date.startsWith("0000-")) {
    return undefined;
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 0) {
    return undefined;
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getEntryTimestamp = (date: string | null | undefined): number => {
  if (!date || date.trim() === "" || date.startsWith("0000-")) {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 0) {
    return Number.NEGATIVE_INFINITY;
  }
  return parsed.getTime();
};

const statusToMilestoneState = (
  status: IntegrationStatusLabel | null
): IntegrationMilestone["state"] => {
  if (!status) return "pending";

  const lower = status.toLowerCase().trim();
  if (
    lower === "completed" ||
    lower === "validated - full approval" ||
    lower === "validated - conditional approval"
  ) {
    return "completed";
  }

  if (lower === "validation - failed") {
    return "on-hold";
  }

  return mapPhaseToMilestoneState(status);
};

/**
 * Collect timeline entries for a phase.
 * Flat phases (Onboarding/Validation): direct array.
 * Nested phases (Training/Development): all subcategory entries when no subcategory is passed.
 */
const getPhaseTimelineEntries = (
  integration: Integration | undefined,
  category: string,
  subcategory?: string
): TimelineEntry[] => {
  if (!integration?.timeline) return [];

  if (subcategory) {
    return getTimelineEntries(integration.timeline, category, subcategory);
  }

  const directEntries = getTimelineEntries(integration.timeline, category);
  if (directEntries.length > 0) {
    return directEntries;
  }

  // Nested category: gather entries from every subcategory
  const categoryKey = Object.keys(integration.timeline).find(
    (key) => key.toLowerCase() === category.toLowerCase()
  );
  if (!categoryKey) return [];

  const categoryValue = integration.timeline[categoryKey];
  if (!categoryValue || Array.isArray(categoryValue) || typeof categoryValue !== "object") {
    return [];
  }

  const collected: TimelineEntry[] = [];
  Object.keys(categoryValue).forEach((key) => {
    if (key === "id" || key === "ID") return;
    const value = categoryValue[key as keyof typeof categoryValue];
    if (!Array.isArray(value)) return;
    collected.push(
      ...(value as TimelineEntry[]).filter((entry) => entry.status !== null)
    );
  });

  return collected;
};

const getPhaseDate = (
  integration: Integration | undefined,
  category: string,
  subcategory?: string
): string | undefined => {
  const entries = getPhaseTimelineEntries(integration, category, subcategory);
  if (entries.length === 0) return undefined;

  // Keep history order (API returns newest submission first) and take the first
  // entry that has a completion date, matching the top row of the phase table.
  const firstCompleted = entries.find(
    (entry) =>
      getEntryTimestamp(entry.completion_date) > Number.NEGATIVE_INFINITY
  );

  if (firstCompleted) {
    return formatTimelineDate(firstCompleted.completion_date);
  }

  // Nothing completed yet: fall back to the first entry's start/created date
  const [first] = entries;

  return (
    formatTimelineDate(first.start_date) ??
    formatTimelineDate(first.created_at)
  );
};

export const buildIntegrationMilestones = (
  integration: Integration | undefined
): IntegrationMilestone[] => {
  const steps: {
    label: string;
    status: IntegrationStatusLabel | null;
    date?: string;
  }[] = [
    {
      label: "Onboarding",
      status: getOnboardingCurrentStatus(integration),
      date: getPhaseDate(integration, "Onboarding"),
    },
    {
      label: "Training",
      status: getTrainingCurrentStatus(integration),
      date: getPhaseDate(integration, "Training"),
    },
    {
      label: "Development",
      status: getDevelopmentCurrentStatus(integration),
      date: getPhaseDate(integration, "Development"),
    },
    {
      label: "Validation",
      status: getValidationCurrentStatus(integration),
      date: getPhaseDate(integration, "Validation"),
    },
  ];

  return steps.map(({ label, status, date }) => {
    const state = statusToMilestoneState(status);

    return {
      label,
      state,
      ...(isNotApplicableStatus(status)
        ? { statusLabel: NOT_APPLICABLE_META_LABEL }
        : {}),
      ...(state === "completed" && date ? { completedDate: date } : {}),
      ...((state === "in-progress" || state === "on-hold") && date
        ? { statusDate: date }
        : {}),
      ...(state === "pending" && date ? { statusDate: date } : {}),
    };
  });
};

export const getTimelineCompletionPercent = (
  milestones: IntegrationMilestone[]
): number => {
  if (milestones.length === 0) return 0;

  // Same rule as partner admin / partner portal project-status blocks:
  // if Validation is completed, overall progress is 100% even when
  // Training/Development remain Not Started.
  const validationMilestone = milestones.find(
    (milestone) => milestone.label.toLowerCase() === "validation"
  );
  if (validationMilestone?.state === "completed") {
    return 100;
  }

  const completedCount = milestones.filter(
    (milestone) => milestone.state === "completed"
  ).length;

  return Math.round((completedCount / milestones.length) * 100);
};

export const countPendingIntegrationTasks = (
  integration: Integration | undefined
): number => {
  if (!integration) return 0;

  const statuses = [
    getOnboardingCurrentStatus(integration),
    getTrainingCurrentStatus(integration),
    getDevelopmentCurrentStatus(integration),
    getValidationCurrentStatus(integration),
  ];

  return statuses.filter(
    (status) => status !== "Completed" && status !== "Not applicable"
  ).length;
};
